/**
 * Geometric Burst — radial polygon explosion (burst) or orbiting swarm.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

export const geometricBurst: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const text = inputs.text ?? "Allyver";
  const sub = inputs.subtext ?? "";
  const variant = (inputs.variant ?? "burst").toLowerCase();

  const hero = typeSize("hero", ctx.format);
  const md = typeSize("md", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const titleId = `${clipPrefix}-title`;
  const subId = `${clipPrefix}-sub`;
  const shapeCount = 14;
  const shapeIds = Array.from({ length: shapeCount }, (_, i) => `${clipPrefix}-sh-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  const shapeColors = [accent, themeColors.fg, withAlpha(accent, 0.7), withAlpha(themeColors.fg, 0.5)];

  const shapes = shapeIds.map((id, i) => {
    const size = 30 + (i % 5) * 12;
    const angle = (i / shapeCount) * 360;
    const isCircle = i % 3 === 0;
    const isTriangle = i % 4 === 1;
    const color = shapeColors[i % shapeColors.length]!;
    const shapeStyle = isCircle
      ? `border-radius:9999px;background:${color};`
      : isTriangle
        ? `clip-path:polygon(50% 0%, 100% 100%, 0% 100%);background:${color};`
        : `border-radius:8px;background:${color};transform-origin:center;`;
    return `<div id="${id}" data-angle="${angle}" style="position:absolute;left:50%;top:50%;width:${size}px;height:${size}px;transform:translate(-50%,-50%);${shapeStyle}box-shadow:0 0 24px ${withAlpha(color, 0.55)};"></div>`;
  }).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;overflow:hidden;padding:80px;">
    <div style="position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;">
      ${shapes}
      <div id="${titleId}" style="position:relative;z-index:2;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${hero * 0.95}px;letter-spacing:-3px;color:${themeColors.fg};text-shadow:0 0 60px ${withAlpha(accent, 0.7)};text-wrap:balance;text-align:center;line-height:0.95;">${escapeHtml(text)}</div>
      ${sub ? `<div id="${subId}" style="position:relative;z-index:2;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.6}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(sub)}</div>` : ""}
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${titleId}`, { opacity: 0, scale: 0.6 }),
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }) : "",
    ...shapeIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.4 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${titleId}`, { opacity: 1, scale: 1, duration: 0.65, ease: "raw:\"back.out(1.8)\"" }, 0.2),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.5));

  shapeIds.forEach((id, i) => {
    const angle = (i / shapeCount) * Math.PI * 2;
    if (variant === "swarm") {
      const radius = 240 + (i % 4) * 30;
      const dx = Math.cos(angle) * radius;
      const dy = Math.sin(angle) * radius;
      tweens.push(t.to(`#${id}`, { opacity: 0.85, x: dx, y: dy, scale: 1, duration: 0.9, ease: "raw:\"power3.out\"" }, 0.0));
      tweens.push(`tl.to(${JSON.stringify("#" + id)},{rotation:360,duration:${clipDurationSec * 2.5},ease:"none",repeat:-1},${clipStartSec.toFixed(4)});`);
      tweens.push(t.to(`#${id}`, { x: Math.cos(angle + 1) * radius, y: Math.sin(angle + 1) * radius, duration: clipDurationSec, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 1.0));
    } else {
      const dist = 460 + (i % 4) * 60;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist;
      tweens.push(t.to(`#${id}`, { opacity: 1, scale: 1, duration: 0.25, ease: "raw:\"power2.out\"" }, 0.05));
      tweens.push(t.to(`#${id}`, { x: dx, y: dy, rotation: 360, opacity: 0, scale: 0.4, duration: Math.max(0.9, clipDurationSec - 0.4), ease: "raw:\"power2.out\"" }, 0.2));
    }
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));
  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
